import { i18nTranslationHelper } from '@/i18n'
import { probeReachabilityOnce, Reachability } from '@/infra/http/connectivity-probe'
import { NetworkError } from '@/infra/http/network-error'

/**
 * Type guard para `NetworkError`. Centraliza o import e dá um ponto único para
 * os handlers de erro decidirem se delegam ao snackbar global (caminho React
 * Query) ou exibem a mensagem amigável (chamada direta) — ver
 * `getImmediateErrorMessage`.
 */
export const isNetworkError = (error: unknown): error is NetworkError =>
  error instanceof NetworkError

export type ResolveErrorMessageOptions = {
  /**
   * Override do GET de reachability. Por padrão usa a URL canônica
   * (ver connectivity-probe); só informe se um fluxo precisar sondar outro
   * endpoint.
   */
  probeUrl?: string
}

/**
 * Anexa o código de correlação à mensagem, só quando ele existe — evita o
 * sufixo vazio "(cód.: )".
 */
const withCorrelationId = (message: string, correlationId?: string): string =>
  correlationId
    ? `${message} ${i18nTranslationHelper.getTComponents('networkErrorSnackbar.codeSuffix', {
        correlationId
      })}`
    : message

const messageForReachability = (verdict: Reachability, correlationId?: string): string => {
  switch (verdict) {
    case 'offline':
      return i18nTranslationHelper.getTComponents('networkErrorSnackbar.offline')
    case 'reachable':
      return withCorrelationId(
        i18nTranslationHelper.getTComponents('networkErrorSnackbar.proxyBlocked'),
        correlationId
      )
    case 'unreachable':
    default:
      return i18nTranslationHelper.getTComponents('networkErrorSnackbar.unreachable')
  }
}

/**
 * Mensagem síncrona imediata, sem esperar o probe. Para erro de rede dá um
 * palpite neutro (offline / inalcançável) que pode ser refinado por
 * `resolveErrorMessage` (ex.: proxyBlocked). Use para feedback instantâneo
 * antes do `await`.
 */
export function getImmediateErrorMessage(error: unknown): string {
  if (!(error instanceof NetworkError)) {
    return error instanceof Error ? error.message : String(error)
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return i18nTranslationHelper.getTComponents('networkErrorSnackbar.offline')
  }
  return i18nTranslationHelper.getTComponents('networkErrorSnackbar.unreachable')
}

/**
 * Resolve a mensagem a ser exibida para um erro qualquer.
 *
 * - Não-NetworkError → usa a própria mensagem do erro.
 * - NetworkError → roda o probe de conectividade (memoizado por erro, então
 *   compartilhado com o report do Sentry) e escolhe a mensagem adequada
 *   (offline / bloqueio de rede-proxy / servidor inalcançável).
 *
 * É async por causa do probe; mantenha o `await` no chamador.
 */
export async function resolveErrorMessage(
  error: unknown,
  options: ResolveErrorMessageOptions = {}
): Promise<string> {
  if (!(error instanceof NetworkError)) {
    return error instanceof Error ? error.message : String(error)
  }

  const verdict = await probeReachabilityOnce(error, options.probeUrl)
  return messageForReachability(verdict, error.correlationId)
}
